import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import * as styles from './locking-scroll.module.css';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PortableText } from '../portableText';

export function LockingScroll({
  backgroundColor,
  eyebrowText,
  lockingScrollItems,
  textColor
}) {
  // state
  const [activeTocItem, setActiveTocItem] = useState(null);

  const heightMarkerEl = useRef();
  const processScrollEl = useRef();

  // hooks
  const isMobile = useMediaQuery('(max-width: 799px)');

  const handleScroll = () => {
    // Initialize variables to track the closest section
    let closestDistance = Infinity;
    let activeItemId = null;

    // Find the closest section to the top of the viewport
    lockingScrollItems.forEach(item => {
      const element = document.getElementById(item._key);

      if (element) {
        const rect = element.getBoundingClientRect();
        const distanceToTop = rect.top;

        if (distanceToTop >= 0 && distanceToTop < closestDistance) {
          closestDistance = distanceToTop;
          activeItemId = item._key;
        }
      }
    });

    if (activeItemId) {
      setActiveTocItem(activeItemId);
    }
  };

  // set active id on load
  useEffect(() => {
    const initialActiveTocItem = lockingScrollItems[0]?._key;
    setActiveTocItem(initialActiveTocItem);
  }, []);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    // Clean up function
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lockingScrollItems]);

  const handleScrollToContent = (event, heading) => {
    event.preventDefault();
    const targetElement = document.getElementById(heading._key);

    if (targetElement) {
      // Find the parent element of the header
      const parentElement = targetElement.parentElement;

      if (parentElement) {
        // Calculate the scroll offset
        const scrollOffset = parentElement.getBoundingClientRect().top - 128; // Adjust the value as needed

        // Manually scroll to the parent section with adjusted vertical positioning
        window.scrollBy({ top: scrollOffset, behavior: 'smooth' });
      }
    }
  };

  return (
    <div
      className={
        classNames(
          backgroundColor && `b${backgroundColor}`,
          styles.moduleContainer,
          textColor
        )
      }
    >
      <div className={styles.parent}>
        <div className={styles.stepsContainer}>
          {eyebrowText && (
            <h2 className='heading-xs' ref={heightMarkerEl}>
              {eyebrowText}
            </h2>
          )}
          <nav className={styles.toc}>
            {lockingScrollItems?.map(heading => (
              <li
                className={styles.tocItem}
                key={heading._key}
              >
                <a
                  className={
                    classNames(
                      'heading-l',
                      activeTocItem === heading._key && styles.active
                    )
                  }
                  href={`#${heading._key}`}
                  onClick={(event) => handleScrollToContent(event, heading)}
                >
                  {heading.header}
                </a>
              </li>
            ))}
          </nav>
        </div>
        <div
          className={styles.descriptionContainer}
          ref={processScrollEl}
          tabIndex={0}
          role='menu'
        >
          {lockingScrollItems?.map(step => {
            const { _key, _rawDescription, header, image } = step;

            const imageData = getImage(image?.asset);
            const previewImage = image?.asset?.url;

            return (
              <div
                className={styles.step}
                key={_key}
              >
                <div
                  className={styles.description}
                  role='menuitem'
                >
                  {imageData && (
                    <GatsbyImage
                      alt={image?.alt || ''}
                      className={styles.image}
                      image={imageData}
                    />
                  )}
                  {!imageData && (
                    <img
                      alt={image?.alt || ''}
                      className={styles.image}
                      src={previewImage}
                    />

                  )}
                  <h3
                    className={
                      classNames(
                        isMobile ? 'heading-l' : 'heading-m',
                        styles.stepName
                      )
                    }
                    id={_key}
                  >
                    {header}
                  </h3>
                  <div className={styles.descriptionText}>
                    <PortableText blocks={_rawDescription} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
